(function () {
  'use strict';

  /* ============================================================
     DateUtils — timezone-safe, date-only helpers
     ============================================================ */
  var DateUtils = {
    nowInZone: function (timeZone) {
      var parts = new Intl.DateTimeFormat('en-US', {
        timeZone: timeZone,
        year: 'numeric', month: 'numeric', day: 'numeric',
        hour: 'numeric', minute: 'numeric', hour12: false
      }).formatToParts(new Date());

      var map = {};
      for (var i = 0; i < parts.length; i++) map[parts[i].type] = parts[i].value;

      return {
        date: new Date(Number(map.year), Number(map.month) - 1, Number(map.day)),
        hour: Number(map.hour) % 24,
        minute: Number(map.minute)
      };
    },

    addDays: function (date, days) {
      var d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      d.setDate(d.getDate() + days);
      return d;
    },

    sameDay: function (a, b) {
      return a.getFullYear() === b.getFullYear() &&
             a.getMonth() === b.getMonth() &&
             a.getDate() === b.getDate();
    },

    formatYYYYMMDD: function (date) {
      var m = String(date.getMonth() + 1).padStart(2, '0');
      var d = String(date.getDate()).padStart(2, '0');
      return date.getFullYear() + '-' + m + '-' + d;
    },

    formatDisplay: function (date) {
      return date.toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric'
      });
    },

    parseYYYYMMDD: function (str) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(str || '')) return null;
      var p = str.split('-');
      var d = new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2]));
      return isNaN(d.getTime()) ? null : d;
    },

    monthLabel: function (date) {
      return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
  };

  /* ============================================================
     Holidays — US federal blackout dates, generated per year
     ============================================================ */
  var Holidays = (function () {
    var cache = {};

    function nthWeekday(year, month, weekday, n) {
      var d = new Date(year, month, 1);
      var offset = (weekday - d.getDay() + 7) % 7;
      return new Date(year, month, 1 + offset + (n - 1) * 7);
    }

    function lastWeekday(year, month, weekday) {
      var d = new Date(year, month + 1, 0);
      var offset = (d.getDay() - weekday + 7) % 7;
      return new Date(year, month, d.getDate() - offset);
    }

    function forYear(year) {
      if (cache[year]) return cache[year];

      var list = [
        new Date(year, 0, 1),
        lastWeekday(year, 4, 1),
        new Date(year, 6, 4),
        nthWeekday(year, 8, 1, 1),
        nthWeekday(year, 10, 4, 4),
        new Date(year, 11, 25)
      ];

      var set = {};
      for (var i = 0; i < list.length; i++) set[DateUtils.formatYYYYMMDD(list[i])] = true;
      cache[year] = set;
      return set;
    }

    return {
      isHoliday: function (date) {
        return !!forYear(date.getFullYear())[DateUtils.formatYYYYMMDD(date)];
      }
    };
  })();

  /* ============================================================
     CartAPI — Shopify Ajax Cart
     ============================================================ */
  var CartAPI = {
    fetchCart: function () {
      return fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) {
          if (!r.ok) throw new Error('Cart fetch failed');
          return r.json();
        });
    },

    saveDeliveryDate: function (attributeName, yyyymmdd) {
      var attributes = {};
      attributes[attributeName] = yyyymmdd;

      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: attributes })
      }).then(function (r) {
        if (!r.ok) throw new Error('Cart update failed');
        return r.json();
      });
    }
  };

  /* ============================================================
     CartProfile — product classification via tags / product type
     ============================================================ */
  var CartProfile = (function () {
    var productCache = {};

    function classifyText(text) {
      var haystack = (text || '').toLowerCase();
      if (haystack.indexOf('non-perishable') !== -1 || haystack.indexOf('non perishable') !== -1) {
        return 'non-perishable';
      }
      if (haystack.indexOf('perishable') !== -1) return 'perishable';
      if (haystack.indexOf('frozen') !== -1) return 'frozen';
      return null;
    }

    function classifyItem(item) {
      var byType = classifyText(item.product_type);
      if (byType) return Promise.resolve(byType);

      var handle = item.handle;
      if (!handle) return Promise.resolve('perishable');

      if (!productCache[handle]) {
        productCache[handle] = fetch('/products/' + handle + '.js', {
          headers: { Accept: 'application/json' }
        })
          .then(function (r) { return r.ok ? r.json() : null; })
          .catch(function () { return null; });
      }

      return productCache[handle].then(function (product) {
        if (!product) return 'perishable';
        var byTags = classifyText((product.tags || []).join(' ') + ' ' + (product.type || ''));
        return byTags || 'perishable';
      });
    }

    function profileFromCart(cart) {
      var items = (cart && cart.items) || [];
      return Promise.all(items.map(classifyItem)).then(buildProfile);
    }

    function buildProfile(classes) {
      return {
        empty: classes.length === 0,
        classes: classes,
        containsPerishable: classes.indexOf('perishable') !== -1,
        containsOnlyFrozen: classes.length > 0 && classes.every(function (c) { return c === 'frozen'; }),
        containsOnlyNonPerishable: classes.length > 0 && classes.every(function (c) { return c === 'non-perishable'; }),
        containsOnlyFrozenOrNonPerishable: classes.length > 0 && classes.every(function (c) {
          return c === 'frozen' || c === 'non-perishable';
        })
      };
    }

    return {
      fromCart: profileFromCart,
      cartContainsPerishable: function (profile) { return profile.containsPerishable; },
      cartContainsOnlyFrozen: function (profile) { return profile.containsOnlyFrozen; },
      cartContainsOnlyNonPerishable: function (profile) { return profile.containsOnlyNonPerishable; }
    };
  })();

  /* ============================================================
     Shipping — zone + estimated arrival abstractions
     ============================================================ */
  var Shipping = {
    getShippingZone: function () {
      return Promise.resolve('FedEx Zone 3');
    },

    getEstimatedArrivalDate: function (fulfillmentDate) {
      return Promise.resolve(DateUtils.addDays(fulfillmentDate, 2));
    }
  };

  /* ============================================================
     Rules — blackout logic + earliest selectable date engine
     
     NEW: customBlackoutDates is injected at Rules creation
     ============================================================ */
  function createRules(customBlackoutDates) {
    /* Convert custom dates array to a fast lookup set (YYYY-MM-DD format) */
    var customBlackoutSet = {};
    if (customBlackoutDates && Array.isArray(customBlackoutDates)) {
      for (var i = 0; i < customBlackoutDates.length; i++) {
        customBlackoutSet[customBlackoutDates[i]] = true;
      }
    }

    return {
      isSunday: function (date) {
        return date.getDay() === 0;
      },

      isMonday: function (date) {
        return date.getDay() === 1;
      },

      isHoliday: function (date) {
        return Holidays.isHoliday(date);
      },

      /* NEW: check custom blackout dates */
      isCustomBlackout: function (date) {
        return !!customBlackoutSet[DateUtils.formatYYYYMMDD(date)];
      },

      /* Updated to include custom blackouts */
      isBlackout: function (date) {
        return this.isSunday(date) || this.isMonday(date) || 
               this.isHoliday(date) || this.isCustomBlackout(date);
      },

      isPastCutoff: function (now, cutoffHour, cutoffMinute) {
        return now.hour > cutoffHour ||
               (now.hour === cutoffHour && now.minute >= cutoffMinute);
      },

      getValidFulfillmentDate: function (now, cutoffHour, cutoffMinute) {
        var d = this.isPastCutoff(now, cutoffHour, cutoffMinute)
          ? DateUtils.addDays(now.date, 1)
          : now.date;

        while (this.isBlackout(d)) d = DateUtils.addDays(d, 1);
        return d;
      },

      getEarliestSelectableDate: function (fulfillmentDate, profile, zone) {
        var self = this;
        if (profile.containsPerishable) {
          return Shipping.getEstimatedArrivalDate(fulfillmentDate, zone)
            .then(function (date) { return self.rollPastBlackouts(date); });
        }

        var zoneAMatch = zone === 'FedEx Zone 3' || zone === 'FedEx Zone 4';
        if (zoneAMatch && profile.containsOnlyFrozenOrNonPerishable) {
          return Promise.resolve(this.rollPastBlackouts(DateUtils.addDays(fulfillmentDate, 2)));
        }

        return Promise.resolve(this.rollPastBlackouts(DateUtils.addDays(fulfillmentDate, 2)));
      },

      rollPastBlackouts: function (date) {
        var d = date;
        while (this.isBlackout(d)) d = DateUtils.addDays(d, 1);
        return d;
      },

      isSelectable: function (date, earliest, latest) {
        return date >= earliest && date <= latest && !this.isBlackout(date);
      }
    };
  }

  /* ============================================================
     Calendar — rendering, navigation, selection, accessibility
     ============================================================ */
  function DeliveryCalendar(root) {
    this.root = root;
    this.input = root.querySelector('[data-ddp-input]');
    this.popup = root.querySelector('[data-ddp-popup]');
    this.grid = root.querySelector('[data-ddp-grid]');
    this.title = root.querySelector('[data-ddp-title]');
    this.status = root.querySelector('[data-ddp-status]');
    this.prevBtn = root.querySelector('[data-ddp-prev]');
    this.nextBtn = root.querySelector('[data-ddp-next]');

    /* Parse custom blackout dates from data attribute */
    var customDatesAttr = root.dataset.customBlackoutDates || '';
    var customDates = customDatesAttr.trim() !== '' 
      ? customDatesAttr.split(',').map(function (s) { return s.trim(); })
      : [];

    this.config = {
      cutoffHour: Number(root.dataset.cutoffHour || 10),
      cutoffMinute: Number(root.dataset.cutoffMinute || 0),
      timeZone: root.dataset.timezone || 'America/New_York',
      attributeName: root.dataset.attributeName || 'Requested Delivery Date',
      monthsVisible: Number(root.dataset.monthsVisible || 2),
      customBlackoutDates: customDates
    };

    /* Create Rules instance with custom blackout dates */
    this.Rules = createRules(this.config.customBlackoutDates);

    this.ready = false;
    this.readyPromise = null;
    this.earliest = null;
    this.latest = null;
    this.viewYear = null;
    this.viewMonth = null;
    this.selected = null;

    this.bindTriggers();

    if (this.input.value.trim() !== '') this.init();
  }

  DeliveryCalendar.prototype = {
    bindTriggers: function () {
      var self = this;

      this.input.addEventListener('click', function () { self.toggle(); });
      this.input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
          e.preventDefault();
          self.open();
        }
        if (e.key.length === 1) e.preventDefault();
      });

      document.addEventListener('click', function (e) {
        if (!self.root.contains(e.target)) self.close();
      });

      this.root.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
          self.close();
          self.input.focus();
        }
      });
    },

    init: function () {
      if (this.readyPromise) return this.readyPromise;

      var self = this;
      var now = DateUtils.nowInZone(this.config.timeZone);
      var fulfillment = this.Rules.getValidFulfillmentDate(now, this.config.cutoffHour, this.config.cutoffMinute);

      this.today = now.date;

      this.readyPromise = Promise.all([CartAPI.fetchCart(), Shipping.getShippingZone()])
        .then(function (results) {
          var cart = results[0];
          var zone = results[1];

          var saved = DateUtils.parseYYYYMMDD(cart.attributes && cart.attributes[self.config.attributeName]);

          return CartProfile.fromCart(cart).then(function (profile) {
            return self.Rules.getEarliestSelectableDate(fulfillment, profile, zone);
          }).then(function (earliest) {
            self.earliest = earliest;
            self.latest = new Date(
              self.today.getFullYear(),
              self.today.getMonth() + self.config.monthsVisible,
              0
            );

            if (saved && self.Rules.isSelectable(saved, self.earliest, self.latest)) {
              self.selected = saved;
              self.input.value = DateUtils.formatDisplay(saved);
            } else if (saved) {
              self.input.value = '';
              CartAPI.saveDeliveryDate(self.config.attributeName, '').catch(function () {});
            }

            var start = self.selected || self.earliest;
            self.viewYear = start.getFullYear();
            self.viewMonth = start.getMonth();
            self.ready = true;
          });
        })
        .catch(function () {
          self.earliest = self.Rules.rollPastBlackouts(DateUtils.addDays(fulfillment, 2));
          self.latest = new Date(
            self.today.getFullYear(),
            self.today.getMonth() + self.config.monthsVisible,
            0
          );
          self.viewYear = self.earliest.getFullYear();
          self.viewMonth = self.earliest.getMonth();
          self.ready = true;
        });

      return this.readyPromise;
    },

    toggle: function () {
      if (this.popup.hidden) this.open();
      else this.close();
    },

    open: function () {
      var self = this;
      this.init().then(function () {
        self.renderCalendar();
        self.popup.hidden = false;

        var rect = self.input.getBoundingClientRect();
        var needed = self.popup.offsetHeight + 16;
        self.popup.classList.toggle('ddp__popup--up',
          window.innerHeight - rect.bottom < needed && rect.top > needed);

        self.input.setAttribute('aria-expanded', 'true');
        self.focusFirstSelectable();
        self.bindNavOnce();
      });
    },

    close: function () {
      this.popup.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
    },

    bindNavOnce: function () {
      if (this.navBound) return;
      this.navBound = true;
      var self = this;
      this.prevBtn.addEventListener('click', function () { self.changeMonth(-1); });
      this.nextBtn.addEventListener('click', function () { self.changeMonth(1); });
      this.grid.addEventListener('keydown', function (e) { self.onGridKeydown(e); });
      this.grid.addEventListener('click', function (e) {
        var btn = e.target.closest('button[data-date]');
        if (btn && !btn.disabled) self.selectDate(DateUtils.parseYYYYMMDD(btn.dataset.date));
      });
    },

    monthHasSelectableDate: function (year, month) {
      var daysInMonth = new Date(year, month + 1, 0).getDate();
      for (var d = 1; d <= daysInMonth; d++) {
        if (this.Rules.isSelectable(new Date(year, month, d), this.earliest, this.latest)) return true;
      }
      return false;
    },

    changeMonth: function (delta) {
      var y = this.viewYear;
      var m = this.viewMonth + delta;
      var target = new Date(y, m, 1);

      if (!this.monthHasSelectableDate(target.getFullYear(), target.getMonth())) return;

      this.viewYear = target.getFullYear();
      this.viewMonth = target.getMonth();
      this.renderCalendar();
      this.focusFirstSelectable();
    },

    renderCalendar: function () {
      var y = this.viewYear;
      var m = this.viewMonth;
      var first = new Date(y, m, 1);
      var daysInMonth = new Date(y, m + 1, 0).getDate();
      var frag = document.createDocumentFragment();

      this.title.textContent = DateUtils.monthLabel(first);

      for (var pad = 0; pad < first.getDay(); pad++) {
        var empty = document.createElement('span');
        empty.className = 'ddp__day ddp__day--empty';
        empty.setAttribute('role', 'presentation');
        frag.appendChild(empty);
      }

      for (var d = 1; d <= daysInMonth; d++) {
        var date = new Date(y, m, d);
        var selectable = this.Rules.isSelectable(date, this.earliest, this.latest);
        var btn = document.createElement('button');

        btn.type = 'button';
        btn.className = 'ddp__day';
        btn.textContent = String(d);
        btn.dataset.date = DateUtils.formatYYYYMMDD(date);
        btn.setAttribute('role', 'gridcell');
        btn.setAttribute('aria-label', date.toLocaleDateString('en-US', {
          weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
        }) + (selectable ? '' : ', unavailable'));

        if (!selectable) {
          btn.disabled = true;
          btn.setAttribute('aria-disabled', 'true');
          btn.tabIndex = -1;
        } else {
          btn.tabIndex = -1;
        }

        if (this.selected && DateUtils.sameDay(date, this.selected)) {
          btn.classList.add('ddp__day--selected');
          btn.setAttribute('aria-selected', 'true');
        }
        if (this.today && DateUtils.sameDay(date, this.today)) {
          btn.classList.add('ddp__day--today');
        }

        frag.appendChild(btn);
      }

      this.grid.textContent = '';
      this.grid.appendChild(frag);

      this.prevBtn.disabled = !this.monthHasSelectableDate(
        m === 0 ? y - 1 : y, m === 0 ? 11 : m - 1
      );
      this.nextBtn.disabled = !this.monthHasSelectableDate(
        m === 11 ? y + 1 : y, m === 11 ? 0 : m + 1
      );
    },

    focusFirstSelectable: function () {
      var target = this.grid.querySelector('.ddp__day--selected:not(:disabled)') ||
                   this.grid.querySelector('button[data-date]:not(:disabled)');
      if (target) {
        target.tabIndex = 0;
        target.focus();
      }
    },

    onGridKeydown: function (e) {
      var current = e.target.closest('button[data-date]');
      if (!current) return;

      var jump = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 }[e.key];

      if (jump) {
        e.preventDefault();
        this.moveFocus(DateUtils.parseYYYYMMDD(current.dataset.date), jump);
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (!current.disabled) this.selectDate(DateUtils.parseYYYYMMDD(current.dataset.date));
      }
    },

    moveFocus: function (fromDate, delta) {
      var step = delta > 0 ? Math.abs(delta) : -Math.abs(delta);
      var date = DateUtils.addDays(fromDate, step);

      for (var i = 0; i < 90; i++) {
        if (this.Rules.isSelectable(date, this.earliest, this.latest)) break;
        date = DateUtils.addDays(date, delta > 0 ? 1 : -1);
      }
      if (!this.Rules.isSelectable(date, this.earliest, this.latest)) return;

      if (date.getFullYear() !== this.viewYear || date.getMonth() !== this.viewMonth) {
        this.viewYear = date.getFullYear();
        this.viewMonth = date.getMonth();
        this.renderCalendar();
      }

      var btn = this.grid.querySelector('button[data-date="' + DateUtils.formatYYYYMMDD(date) + '"]');
      if (btn && !btn.disabled) {
        var prev = this.grid.querySelector('button[tabindex="0"]');
        if (prev) prev.tabIndex = -1;
        btn.tabIndex = 0;
        btn.focus();
      }
    },

    selectDate: function (date) {
      var self = this;

      if (!date || !this.Rules.isSelectable(date, this.earliest, this.latest)) {
        this.setStatus('That date is not available for delivery.');
        return;
      }

      var yyyymmdd = DateUtils.formatYYYYMMDD(date);
      this.selected = date;
      this.input.value = DateUtils.formatDisplay(date);
      this.renderCalendar();
      this.setStatus('Saving…');

      CartAPI.saveDeliveryDate(this.config.attributeName, yyyymmdd)
        .then(function () {
          self.setStatus('Delivery date saved.');
          self.close();
          self.input.focus();
        })
        .catch(function () {
          self.setStatus('Could not save. Please try again.');
        });
    },

    setStatus: function (msg) {
      if (this.status) this.status.textContent = msg;
    }
  };

  /* ============================================================
     Boot — one instance per section on the page
     ============================================================ */
  function boot() {
    var roots = document.querySelectorAll('[data-delivery-date-picker]');
    for (var i = 0; i < roots.length; i++) {
      if (!roots[i].__ddp) roots[i].__ddp = new DeliveryCalendar(roots[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
